<template>
  <div
    :class="[
      className,
      !!value && `${className}--filled`,
      showError && (!!errorMsg || !!errorSlot) && `${className}--error`
    ]"
  >
    <label
      :class="[
        `${className}__wrapper`,
        dropActive && `${className}__dropping`,
        !dropSupported && `${className}__dropdisabled`
      ]"
    >
      <input
        ref="input"
        :class="`${className}__input`"
        type="file"
        v-bind="$attrs"
        v-on="$listeners"
        :accept="acceptedFileTypes"
        @invalid="onInvalid"
        @input.stop="onInput"
      />
      <div
        :class="`${className}__content`"
        @drop.prevent="dropFiles"
        @dragover.prevent
        @dragenter.prevent="dragEnter"
        @dragleave.prevent="dragLeave"
      >
        <template v-if="this.$scopedSlots.custom">
          <slot name="custom" :files="files"></slot>
        </template>
        <template v-else>
          <Icon upload class="v-file__upload" />
          <div class="v-file__label">{{ placeholder }}</div>
        </template>
      </div>
    </label>
    <template v-if="!this.$scopedSlots.custom">
      <div class="v-file__helper">
        {{ showError ? errorMsg || errorSlot : description }}
      </div>
      <div
        class="v-file__list"
        v-for="(file, index) in this.files"
        v-bind:key="'file' + index"
      >
        <div class="v-file__listitem">
          <div class="v-file__name">{{ file.name }}</div>
          <Icon remove class="v-file__remove" @click="removeFile(file)" />
        </div>
      </div>
      <div v-if="!mounted" class="v-file__slot" ref="error">
        <slot></slot>
      </div>
    </template>
    <template v-if="dropSupported && droppableFullscreen">
      <portal to="modals">
        <div
          :class="[
            'v-file__fullscreen-dropzone',
            dropActive && 'v-file__fullscreen-dropzone--active'
          ]"
          @dragenter.prevent
          @dragover.prevent="dragOver"
          @dragleave.prevent="dragLeave"
          @drop.prevent="dropFiles"
        >
          <Icon drop />
          <v-text size="large" class="mt-4">{{
            $t(
              "Add files by dropping them in this window",
              "components.file.drop-message"
            )
          }}</v-text>
        </div>
      </portal>
    </template>
  </div>
</template>

<script>
import { default as resetMixin } from "../v-form/mixin.reset";
import { default as mixins } from "../../mixins";
import Icon from "./icon";

export default {
  inheritAttrs: false,
  mixins: [resetMixin],
  components: {
    Icon
  },
  data() {
    return {
      value: this.$attrs.value,
      files: [],
      filesSizeMax: this.$props.maxSize * 10 ** 6,
      dropActive: false,
      errorSlot: false,
      mounted: false,
      showError: false,
      errorMsg: this.$props.error
    };
  },
  created() {
    this.showError = this.invalid !== false;
  },
  mounted() {
    const error = this.$refs.error && this.$refs.error.innerText;

    if (error) {
      this.errorSlot = error.trim();
      this.showError = true;
    }

    this.mounted = true;
    this.$refs.input.addEventListener("change", this.onChange);

    if (this.dropSupported && this.droppableFullscreen) {
      document.addEventListener("dragenter", this.dragEnter);
    }
  },
  beforeDestroy() {
    if (this.dropSupported && this.droppableFullscreen) {
      document.removeEventListener("dragenter", this.dragEnter);
    }
    this.$refs.input.removeEventListener("change", this.onChange);
  },
  methods: {
    onReset() {
      this.value = "";
      this.files = [];
    },
    onInput(event) {
      if (this.showError) this.showError = false;
      this.value = event.target.value;
    },
    onChange(event) {
      this.addFiles(event.target.files);
    },
    onInvalid(event) {
      event.preventDefault();
      this.showError = true;
    },
    dragEnter() {
      if (!this.droppable) return;
      this.dropActive = true;
    },
    dragOver(e) {
      e.dataTransfer.dropEffect = "copy";
    },
    dragLeave() {
      if (!this.droppable) return;
      this.dropActive = false;
    },
    dropFiles(e) {
      if (!this.dropSupported) return;

      let droppedFiles = e.dataTransfer.files;
      if (droppedFiles) this.addFiles(droppedFiles);
      this.dropActive = false;
    },
    isDuplicate(file) {
      if (this.files.includes(file)) return true;

      // DnD file objects are always unique and not found with includes
      const foundByDetails = this.files.find(
        oldFile =>
          oldFile.name === file.name &&
          oldFile.lastModified === file.lastModified
      );

      return !!foundByDetails;
    },
    addFiles(newFiles) {
      [...newFiles].forEach(file => {
        if (!this.isDuplicate(file)) {
          this.files.push(file);
        }
      }, this);

      this.assignFiles();
      this.$emit("files", this.files);
    },
    removeFile(file) {
      this.files = this.files.filter(f => f != file);
      this.assignFiles();
      this.$emit("files", this.files);
    },
    assignFiles() {
      if (!this.dropSupported) return;
      let dt = new DataTransfer();
      this.files.forEach(file => dt.items.add(file));
      this.fileInput.files = dt.files;
    },
    showMaxSizeError() {
      const errorMsg = mixins.$t(
        "Total max file size reached",
        "components.file.max-size-error",
        { maxSize: `${this.maxSize}MB` }
      );

      this.errorMsg = errorMsg;
      this.showError = true;
      this.fileInput.setCustomValidity("max-size-error");
      this.$emit("error", errorMsg);
    },
    hideMaxSizeError() {
      this.showError = false;
      this.errorMsg = this.error;
      this.fileInput.setCustomValidity("");
      this.$emit("error", null);
    },
    validateMaxSize(newFiles) {
      if (!this.filesSizeMax) return;

      const filesSize = newFiles.reduce((total, file) => total + file.size, 0);
      if (filesSize > this.filesSizeMax) {
        this.showMaxSizeError();
      } else {
        this.hideMaxSizeError();
      }
    }
  },
  watch: {
    files(newFiles) {
      this.validateMaxSize(newFiles);
    }
  },
  computed: {
    dropSupported() {
      try {
        const dt = new DataTransfer();
        return this.droppable;
      } catch (e) {
        return false;
      }
    },
    fileInput() {
      return this.$refs.input;
    },
    className() {
      if (this.$scopedSlots.custom) {
        return "v-file-custom";
      } else {
        return "v-file";
      }
    },
    acceptedFileTypes() {
      if (!this.accept) return;

      // Make sure extensions are prepended with a dot
      const acceptedTypes = this.accept
        .split(",")
        .map(type => {
          if (type.trim().charAt(0) !== ".") {
            return `.${type.trim()}`;
          } else {
            return type;
          }
        })
        .join(",");

      return acceptedTypes;
    }
  },
  props: {
    placeholder: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    droppable: {
      type: Boolean,
      default: true
    },
    droppableFullscreen: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: null
    },
    accept: {
      type: String,
      default: null
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-file {
  user-select: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;

  &__slot {
    display: none;
  }

  &__wrapper {
    position: relative;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    min-height: 52px;
    background: theme-color("white");
    border-radius: 5px;
    border: dashed 2px rgba(theme-color("dark"), 0.3);
    cursor: pointer;
    transition: all 200ms ease-out;
    color: theme-color("placeholder");

    .v-file--error & {
      border-color: theme-color("danger");
    }
  }

  &__label {
    pointer-events: none;
    margin-left: 0.5rem;
  }

  &__helper {
    margin-top: 5px;
    font-size: 0.75rem;

    .v-file--error & {
      color: theme-color("danger");
    }
  }

  &__listitem {
    display: flex;
    align-items: center;
  }

  &__upload {
    flex-shrink: 0;
  }

  &__remove {
    cursor: pointer;
  }

  &__name {
    max-width: 90%;
    margin-right: $spacer;
  }

  &__input {
    position: absolute !important;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);

    &:invalid + .v-file__content {
      box-shadow: none;
    }

    &:disabled + .v-file__content {
      opacity: 0.5;
      color: theme-color("gray-dark");
      cursor: not-allowed;
    }

    &:focus + .v-file__content,
    &:active + .v-file__content,
    .v-file__dropping & + .v-file__content {
      border-color: theme-color("focus");
      color: theme-color("focus");

      .v-input--error & {
        border-color: theme-color("danger");
      }
    }
  }

  &__dropdisabled {
    &.v-file__dropping .v-file__content {
      color: theme-color("danger");
      border-color: theme-color("danger");
      cursor: not-allowed;
    }
  }

  &__fullscreen-dropzone {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1500;
    display: flex;
    pointer-events: none;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    color: theme-color("white");
    opacity: 0;
    background-color: theme-color("focus");
    transition: opacity 500ms ease-in-out;

    &--active {
      opacity: 1;
      pointer-events: all;

      * {
        pointer-events: none;
      }
    }
  }
}

.v-file-custom {
  user-select: none;
  position: relative;

  &__content {
    cursor: pointer;
  }

  &__input {
    position: absolute !important;
    height: 0px;
    width: 0px;
    opacity: 0;
  }
}
</style>
